import type { BehaviorAnimationPayload } from "../../../util/src/blueprint/runtime/types.js";

type BaseAnimationState = {
  x?: number;
  y?: number;
  rotation?: number;
  skewX?: number;
  skewY?: number;
  scaleX?: number;
  scaleY?: number;
  opacity?: number;
  width?: number;
  height?: number;
};

/**
 * Convertit un payload Behavior en paramètres Web Animations API.
 * Retourne directement des `keyframes` + `options` utilisables par `element.animate(...)`.
 */
export function behaviorAnimationToWebAnimation(
  payload: BehaviorAnimationPayload,
  scale: number,
  base: BaseAnimationState = {},
): { keyframes: Keyframe[]; options: KeyframeAnimationOptions } {
  const state = {
    x: base.x ?? 0,
    y: base.y ?? 0,
    rotation: base.rotation ?? 0,
    skewX: base.skewX ?? 0,
    skewY: base.skewY ?? 0,
    scaleX: base.scaleX ?? 1,
    scaleY: base.scaleY ?? 1,
    opacity: base.opacity ?? 1,
    width: base.width,
    height: base.height,
  };

  const keyframes: Keyframe[] = payload.keyframes.map((stop) => {
    const v = stop.values;

    // Persist values frame-to-frame (si non défini dans le stop courant)
    if (typeof v.x === "number") state.x = v.x;
    if (typeof v.y === "number") state.y = v.y;
    if (typeof v.rotation === "number") state.rotation = v.rotation;
    if (typeof v.skewX === "number") state.skewX = v.skewX;
    if (typeof v.skewY === "number") state.skewY = v.skewY;
    if (typeof v.opacity === "number") state.opacity = v.opacity;
    if (typeof v.width === "number") state.width = v.width;
    if (typeof v.height === "number") state.height = v.height;

    // scale (global) override scaleX/scaleY si présent
    if (typeof v.scale === "number") {
      state.scaleX = v.scale;
      state.scaleY = v.scale;
    }
    if (typeof v.scaleX === "number") state.scaleX = v.scaleX;
    if (typeof v.scaleY === "number") state.scaleY = v.scaleY;

    const transform = [
      `translate(${state.x * scale}px, ${state.y * scale}px)`,
      `rotate(${state.rotation}deg)`,
      `skewX(${state.skewX}deg)`,
      `skewY(${state.skewY}deg)`,
      `scale(${state.scaleX}, ${state.scaleY})`,
    ].join(" ");

    return {
      offset: Math.max(0, Math.min(1, stop.offset / 100)), // offset en % -> [0..1]
      easing: stop.easing,
      transform,
      opacity: state.opacity,
      width:
        typeof state.width === "number"
          ? `${state.width * scale}px`
          : undefined,
      height:
        typeof state.height === "number"
          ? `${state.height * scale}px`
          : undefined,
    };
  });

  const options: KeyframeAnimationOptions = {
    duration: payload.durationMs,
    delay: payload.delayMs,
    iterations: payload.iterationCount,
    direction: payload.direction,
    fill: payload.fillMode,
  };

  return { keyframes, options };
}

/**
 * Builds inverse translate keyframes to visually pin text in the viewport
 * while its parent node animation moves along X/Y.
 */
export function behaviorAnimationToViewportCompensation(
  payload: BehaviorAnimationPayload,
  scale: number,
  base: Pick<
    BaseAnimationState,
    "x" | "y" | "skewX" | "skewY" | "width" | "height"
  > = {},
): { keyframes: Keyframe[]; options: KeyframeAnimationOptions } {
  const state = {
    x: base.x ?? 0,
    y: base.y ?? 0,
    skewX: base.skewX ?? 0,
    skewY: base.skewY ?? 0,
    width: base.width,
    height: base.height,
  };

  const keyframes: Keyframe[] = payload.keyframes.map((stop) => {
    const v = stop.values;
    if (typeof v.x === "number") state.x = v.x;
    if (typeof v.y === "number") state.y = v.y;
    if (typeof v.skewX === "number") state.skewX = v.skewX;
    if (typeof v.skewY === "number") state.skewY = v.skewY;
    if (typeof v.width === "number") state.width = v.width;
    if (typeof v.height === "number") state.height = v.height;

    const scaleX =
      typeof state.width === "number" &&
      typeof base.width === "number" &&
      state.width !== 0
        ? base.width / state.width
        : 1;
    const scaleY =
      typeof state.height === "number" &&
      typeof base.height === "number" &&
      state.height !== 0
        ? base.height / state.height
        : 1;

    return {
      offset: Math.max(0, Math.min(1, stop.offset / 100)),
      easing: stop.easing,
      transform: `translate(${-state.x * scale}px, ${-state.y * scale}px) skewX(${-state.skewX}deg) skewY(${-state.skewY}deg) scale(${scaleX}, ${scaleY})`,
    };
  });

  const options: KeyframeAnimationOptions = {
    duration: payload.durationMs,
    delay: payload.delayMs,
    iterations: payload.iterationCount,
    direction: payload.direction,
    fill: payload.fillMode,
  };

  return { keyframes, options };
}
